import { useDebouncedCallback, useDebouncedValue } from '@mantine/hooks';
import ConstructionIcon from '@mui/icons-material/Construction';
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ColorInput } from 'components/Inputs/ColorInput/ColorInput';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { useGetFetchAndFormatAuthors } from 'components/Search/components/SearchAdvance/components/hooks';
import dayjs, { type Dayjs } from 'dayjs';
import { isValidHexColor } from 'components/Inputs/ColorInput/ColorInput';
import { CoreFieldEnumOpt } from 'providers/contexts/extensions/DynamicFieldData';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveSxProps } from 'utility/styles';
import { LabelWithTooltip } from './components/LabelWithTooltip';
import { OperationSelector } from './components/OperationSelector';
import { SearchField } from './components/SearchField';
import { SelectedItemsAccordion } from './components/SelectedItemsAccordion';
import {
  DynamicFieldProvider,
  DynamicFieldProviderProps,
  useDynamicFieldContext,
} from './DynamicFieldContext';
import { DateRangeField } from './fields/DateRangeField';
import { DenumField } from './fields/DenumField';
import { DenumTreeField } from './fields/DenumTreeField';
import { EnumDynamicField } from './fields/EnumDynamicField';
import { GroupDynamicField } from './fields/GroupDynamicField';
import { ImageDynamicField } from './fields/ImageDynamicField';
import { MultitextDynamicField } from './fields/MultitextDynamicField';
import { TextDynamicField } from './fields/TextDynamicField';
import { getTooltipText, handleRemoveChip } from './utility/functions';
import { useTranslation } from 'react-i18next';

/**
 * Date range min/max defaults
 */
export type TDateRangeDefault = {
  min: string;
  max: string;
};

/**
 * Field Access Config
 * Object containing properties defining field visibility to the UI
 *
 * readonly - boolean, set to false by default.
 * If set to true, the UX will present the field as disabled but can still
 * push the value of the read-only field back to the server. The value shall
 * be selected with the default config value - if not set no value should be sent back.
 *
 * reason - object provides additional context
 * reason.type - string enum indicating the reason for read-only status
 * reason.message - human readable label "Requires Audience+" or "Requires xyz permission"
 *
 */
export type FieldAccessConfig = {
  readonly?: boolean;
  reason?: {
    type?: 'permission_denied' | 'software_required' | string;
    message?: string;
  };
};

/**
 * Dynamic Fields Spec
 * Configs vary by field type, see full docs:
 * https://townnews.atlassian.net/wiki/spaces/COSMOS/pages/2881847298/
 * typegen: components['schemas']['core.field.spec.field']
 *
 * Common field properties:
 * type - field type
 * name - field name to associate with its value on submission
 * label - field label to display to the user
 * required - whether the field is required
 * sensitive - field should be redacted (eg. data-private, password fields)
 * help - help text to display to the user
 * tooltip - tooltip text to display to the user
 * icon - the icon to display next to the field
 * access - object relevant to ux presentation (readonly, perms info)
 * default - the field's default value
 *
 * Common config properties:
 * config - object containing field type specific configuration properties
 * config.allow_include_children - whether to show the "include children" option for tree fields
 * config.nodes - the nodes for tree fields
 * config.multiple - whether the field allows multiple values (e.g. multi-select)
 * config.options - the options for enumeration fields
 * config.true_label - custom label for the "true" value of boolean fields
 * config.false_label - custom label for the "false" value of boolean fields
 * config.resolver - the resolver to fetch dynamic options for denum and dtree fields
 * config.app - the app to fetch dynamic options for denum and dtree fields
 * config.params - additional params to fetch dynamic options for denum and dtree fields
 * config.min - minimum value for number and date fields
 * config.max - maximum value for number and date fields
 * config.resolution - the resolution for date fields (date vs date-time)
 * config.relative - whether the date accepts relative (eg "3 days ago")
 *
 */

/**
 * Default value for Dynamic Fields
 * Taken from generated type, "any" used for flexibility
 * as this can render a wide varieity of field/value types.
 */
type DynamicFieldValue =
  | any
  | string
  | number
  | boolean
  | Record<string, any>
  | any[]
  | null;

/**
 * Field Operation
 * operation: "==", "!=", "<=", ">="  etc
 * label: "Equals", "Not Equals", etc
 */
export type DynamicFieldOperation = {
  operation: string;
  label: string;
};

/**
 * Nodes Type
 */
export type TFieldNode = {
  id: number;
  value: string;
  label: string;
  help: string | null;
  icon: string | null;
  leaf: boolean;
};

/**
 * Dynamic Field Type
 */
export type DynamicFieldType = {
  type: string;
  name: string;
  label: string;
  required: boolean;
  sensitive: boolean;
  help: string | null;
  tooltip: string | null;
  icon: string | null;
  default: DynamicFieldValue;
  access?: FieldAccessConfig;
  config: {
    application?: string;
    app_prefix?: boolean;
    allow_subtype?: boolean;
    collapsed?: boolean;
    children?: DynamicFieldType[];
    options?: CoreFieldEnumOpt[];
    allow_include_children?: boolean;
    nodes?: TFieldNode[];
    multiple?: boolean;
    true_label?: string;
    false_label?: string;
    resolver?: string;
    app?: string;
    params?: Record<string, string>;
    data_type?: 'string' | 'integer' | 'float' | 'boolean' | string;
    format?: string;
    /** Date times */
    relative?: boolean;
    resolution?: 'date-time' | 'date' | string;
    step?: string;
    min?: string | number;
    max?: string | number;
    /** Conditionally added props from within NXT */
    hideLabel?: boolean;
    maxDate?: Dayjs | undefined;
    minDate?: Dayjs | undefined;
  };
};

export const DYNAMIC_OPERATORS = ['<dynamic>', '<dynamic_wildcard>'];

/**
 * Dynamic Field Options
 * All optional UX opts for dynamic fields component.
 * Note: not via server, props set by admin code.
 */
export type FieldOptions = {
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  denumView?: 'dropdown' | 'domain' | undefined;
};

/**
 * Meta passed up onChange for some dynamic field types
 * used by parent components to determine what to do with the change
 * options - the dynamic options for the field when the change occured
 * passed by DenumField, so callers can lookup the full obj via value/id as needed
 */
export type TOnChangeMeta = {
  options?: CoreFieldEnumOpt[];
  event?: React.ChangeEvent<HTMLInputElement> | undefined;
};

/**
 * onChange:
 * name - field name (eg: via field config, some rhf path)
 * value - field value, can be string, number, boolean, object, array, etc depending on field type
 * meta - optional meta passed up at time of onchange call
 */
export type TDynamicFieldOnChange = (
  name: string,
  value: any,
  meta?: TOnChangeMeta,
) => void;

/**
 * Dynamic Field Component Props
 */
export type DynamicFormFieldProps = {
  value?: any;
  fieldData?: {
    value: any;
    operation?: string;
  };
  onChange: TDynamicFieldOnChange;
  onChangeOperation?: (operation: string) => void;
  onValidationChange?: (fieldName: string, isValid: boolean) => void;
  operations?: { operation: string; label: string }[];
  disabled?: boolean;
  fieldOptions?: FieldOptions;
  fieldSx?: SxProps<Theme>;
};

/**
 * Base Dynamic Form Field Component
 * - Holds various handlers, states, etc
 * - Renders field with DynamicFieldRenderer
 */
export const DynamicFormField = memo(
  ({
    value,
    fieldData,
    onChange,
    onChangeOperation,
    onValidationChange,
    disabled = false,
    operations = [],
    fieldOptions = {},
    // TODO: this is not used (this is its inception point).
    fieldSx,
  }: DynamicFormFieldProps) => {
    const {
      selectedValues,
      updateSelectedValues,
      fieldConfig,
      includeChildren,
      setIncludeChildren,
    } = useDynamicFieldContext();

    const [selectedValue, setSelectedValue] = useState(
      fieldData?.value ?? value ?? fieldConfig?.default,
    );

    const [selectedOperator, setSelectedOperator] = useState(
      fieldData?.operation || operations[0]?.operation || '',
    );

    const [isUserEditing, setIsUserEditing] = useState(false);

    const isTextField = ['text', 'textarea', 'number'].includes(fieldConfig?.type);

    const [fieldText, setFieldText] = useState(value ?? '');

    const debouncedUpdate = useDebouncedCallback((val: string) => {
      if (isTextField && val !== selectedValue) {
        handleValueChange(fieldConfig?.name, val);
      }
    }, 300);

    const isColorField = fieldConfig?.type === 'color';
    const [colorText, setColorText] = useState(() => {
      return value ?? '';
    });
    const isValidColor =
      colorText === '' ||
      (isValidHexColor(colorText) && String(colorText).startsWith('#'));
    const [debouncedColorText] = useDebouncedValue(colorText, 200);
    const hasInitializedColor = useRef(false);

    // Denum and dtree types are not always accurately
    // represented in the API so we use the data.
    const isDenum = useMemo(
      () =>
        Boolean(fieldConfig?.config?.resolver) && !Boolean(fieldConfig?.config?.nodes),
      [fieldConfig],
    );

    const isDtree = useMemo(
      () => Boolean(fieldConfig?.config?.resolver) && Boolean(fieldConfig?.config?.nodes),
      [fieldConfig],
    );

    const tooltipText = useMemo(() => getTooltipText(fieldConfig), [fieldConfig]);

    // Helper to check if a value contains dynamic operators
    const checkIsDynamicOperator = useCallback((val: any): boolean => {
      // Check for dtree format: { items: ['<dynamic>'], ... }
      if (val?.items && Array.isArray(val.items)) {
        return val.items.length === 1 && DYNAMIC_OPERATORS.includes(val.items[0]);
      }
      // Check for array format: ['<dynamic>']
      if (Array.isArray(val)) {
        return val.length === 1 && DYNAMIC_OPERATORS.includes(val[0]);
      }
      // Check for direct string value: '<dynamic>'
      return DYNAMIC_OPERATORS.includes(val);
    }, []);

    const [isDynamicOperator, setIsDynamicOperator] = useState(() =>
      checkIsDynamicOperator(fieldData?.value ?? value),
    );

    const normalizeBoolean = useCallback((val: any) => {
      if (val === 'true' || val === '1') return true;
      if (val === 'false' || val === '0') return false;
      return val;
    }, []);

    // Handle field config default value
    const [trueValue, falseValue] = useMemo(() => {
      return fieldConfig.default === '1' || fieldConfig.default === '0'
        ? ['1', '0']
        : [true, false];
    }, [fieldConfig.default]);

    // Send values to parent component (TDynamicFieldOnChange)
    const handleValueChange = useCallback(
      (name: string, newValue: any, meta?: TOnChangeMeta) => {
        updateSelectedValues(newValue);
        onChange(name, newValue, meta);
      },
      [onChange, updateSelectedValues],
    );

    // Handle boolean switch change
    const handleSwitchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Store boolean only
        const checked = e.target.checked;
        handleValueChange(fieldConfig.name, checked);
      },
      [fieldConfig.name, handleValueChange],
    );

    // Handle radio button change
    const handleRadioChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawVal = e.target.value;
        const normalized = normalizeBoolean(rawVal);
        handleValueChange(fieldConfig.name, normalized);
      },
      [fieldConfig.name, handleValueChange, normalizeBoolean],
    );

    // Allow for update from fieldData changes outside of user edit
    useEffect(() => {
      if (['text', 'textarea', 'number'].includes(fieldConfig.type) && !isUserEditing) {
        const newVal = fieldData?.value ?? value ?? fieldConfig?.default ?? '';
        setFieldText(newVal);
      }
    }, [fieldData?.value, value, fieldConfig?.default, fieldConfig.type, isUserEditing]);

    useEffect(() => {
      const incomingVal = fieldData?.value ?? value ?? fieldConfig?.default;
      const normalizedVal =
        fieldConfig.type === 'boolean' ? normalizeBoolean(incomingVal) : incomingVal;

      setSelectedValue(normalizedVal);
      setSelectedOperator(fieldData?.operation || operations[0]?.operation || '');
    }, [
      fieldData,
      value,
      fieldConfig?.default,
      operations,
      fieldConfig.type,
      normalizeBoolean,
    ]);

    // Update isDynamicOperator when field data changes
    useEffect(() => {
      const currentValue = fieldData?.value ?? value ?? selectedValues;
      const isDynamic = checkIsDynamicOperator(currentValue);
      setIsDynamicOperator(isDynamic);
    }, [fieldData?.value, value, selectedValues, checkIsDynamicOperator]);

    // Color field update on debounce
    useEffect(() => {
      if (!isColorField || disabled) {
        return;
      }

      if (!hasInitializedColor.current) {
        hasInitializedColor.current = true;
        return;
      }

      const isValid =
        debouncedColorText === '' ||
        (isValidHexColor(debouncedColorText) &&
          String(debouncedColorText).startsWith('#'));

      onValidationChange?.(fieldConfig.name, isValid);

      // Check prevents dirty on init
      if (debouncedColorText !== selectedValue) {
        handleValueChange(fieldConfig.name, debouncedColorText);
      }
    }, [
      debouncedColorText,
      selectedValue,
      isColorField,
      fieldConfig.name,
      handleValueChange,
      onValidationChange,
      disabled,
    ]);

    const handleOperatorChange = useCallback(
      (operator: string) => {
        /**
         * Dynamic operators are not operatores but masquerading values
         * When a dynamic operator is selected, we need to set the operator to '=='
         * and the value of the field to the dynamic operator itself
         */
        if (DYNAMIC_OPERATORS.includes(operator)) {
          setIsDynamicOperator(true);
          setIncludeChildren(false);
          setSelectedOperator('==');
          onChangeOperation?.('==');

          const dynamicValue = isDtree
            ? { items: [operator], include_children: false }
            : operator;

          handleValueChange(fieldConfig.name, dynamicValue);
        } else {
          // Clear dynamic operator masquerade first
          if (isDynamicOperator) {
            updateSelectedValues([]);
            onChange(fieldConfig.name, []);
          }

          // Then set the operator
          setIsDynamicOperator(false);
          setSelectedOperator(operator);
          onChangeOperation?.(operator);
        }
      },
      [
        fieldConfig.name,
        handleValueChange,
        isDtree,
        isDynamicOperator,
        onChange,
        onChangeOperation,
        setIncludeChildren,
        updateSelectedValues,
      ],
    );

    const onRemoveChip = (optionValue: string) => {
      const newSelectedValues = handleRemoveChip(selectedValues as string[], optionValue);
      if (fieldConfig.config?.multiple) {
        // Ensure items object is maintained for Dtree
        const updatedValues = isDtree
          ? { items: newSelectedValues, include_children: includeChildren }
          : newSelectedValues;
        // If not Dtree, set the new values directly
        updateSelectedValues(updatedValues);
        onChange(fieldConfig.name, updatedValues);
      } else {
        updateSelectedValues([]);
        onChange(fieldConfig.name, []);
      }
    };

    const normalizedSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];

    const handleIncludeChildrenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setIncludeChildren(isChecked);
      onChange(fieldConfig.name, {
        items: normalizedSelectedValues,
        ...(isChecked && { include_children: true }),
      });
    };

    // Dynamic container testid
    const dynamicTestId = useMemo(() => {
      const cleanId = fieldConfig?.name?.replace(/[^a-z0-9-_]/gi, '-');
      return `dynamic-field-${cleanId}`;
    }, [fieldConfig?.name]);

    // Any specific denum view handling
    const isDomainDenum = useMemo(() => {
      return isDenum && fieldOptions?.denumView === 'domain';
    }, [isDenum, fieldOptions?.denumView]);

    const isDropdownDenum = useMemo(() => {
      return isDenum && fieldOptions?.denumView === 'dropdown';
    }, [isDenum, fieldOptions?.denumView]);

    // Check if we should display the selected area
    // for denum/dtree fields with special handling
    // for some components that have alt views.
    const showSelectItemsArea = useMemo(() => {
      // Selections area is only for denum/dtrees
      const isDenumOrDtree = isDenum || isDtree;
      if (!isDenumOrDtree) {
        return false;
      }
      // Some denum components display selections in an
      // alternative area within the component itself
      const isAltDenumView = isDomainDenum || isDropdownDenum;
      if (isAltDenumView) {
        return false;
      }
      // Standard view check
      const isShown = isDenumOrDtree && !isDynamicOperator;
      return isShown;
    }, [isDenum, isDomainDenum, isDropdownDenum, isDtree, isDynamicOperator]);

    return (
      <Box
        data-testid="dynamic-form-field"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas: `
              "opselector"
              "selectheader"
            `,
          }}
        >
          {operations?.length > 1 && (
            <Box
              sx={{
                gridArea: 'opselector',
              }}
            >
              <OperationSelector
                operations={operations}
                selectedOperator={selectedOperator}
                onOperatorChange={handleOperatorChange}
                fieldData={fieldData}
              />
            </Box>
          )}

          {showSelectItemsArea && (
            <Box
              sx={{
                gridArea: 'selectheader',
                display: 'flex',
                flexDirection: 'column',
                pb: '4px',
              }}
            >
              <LabelWithTooltip
                label={fieldConfig.label}
                tooltip={tooltipText}
              />
              {
                // Until API config is provided to specify if query is supported for a given field
                fieldConfig.name === 'tag_keyword' && <SearchField disabled={disabled} />
              }
              <SelectedItemsAccordion
                onRemove={onRemoveChip}
                includeChildren={includeChildren}
                onIncludeChildrenChange={handleIncludeChildrenChange}
                includeChildrenDisabled={isDynamicOperator}
                disabled={disabled}
              />
            </Box>
          )}
        </Box>

        <Box
          data-testid={dynamicTestId}
          sx={{
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {!isDynamicOperator && (
            <DynamicFieldRenderer
              fieldConfig={fieldConfig}
              tooltipText={tooltipText}
              colorText={colorText}
              setColorText={setColorText}
              disabled={disabled}
              isValidColor={isValidColor}
              selectedValue={selectedValue}
              fieldOptions={fieldOptions}
              fieldSx={fieldSx}
              fieldText={fieldText}
              setFieldText={setFieldText}
              setIsUserEditing={setIsUserEditing}
              isDynamicOperator={isDynamicOperator}
              handleValueChange={handleValueChange}
              trueValue={trueValue}
              falseValue={falseValue}
              handleSwitchChange={handleSwitchChange}
              handleRadioChange={handleRadioChange}
              debouncedUpdate={debouncedUpdate}
              isDtree={isDtree}
              onValidationChange={onValidationChange}
            />
          )}
        </Box>
      </Box>
    );
  },
);
DynamicFormField.displayName = 'DynamicFormField';

/**
 * --------------------------------------------------
 *  Dynamic Field Renderer
 * --------------------------------------------------
 * - Renders the appropriate field type based on fieldConfig
 * - Memoized for more stable field rendering
 * - Props are mostly handlers via DynamicFormField
 */

export type DynamicFieldRendererProps = {
  fieldConfig: DynamicFieldType;
  tooltipText: string | null;
  colorText: string;
  setColorText: React.Dispatch<React.SetStateAction<string>>;
  disabled: boolean;
  isValidColor: boolean;
  selectedValue: any;
  fieldText: string;
  setFieldText: React.Dispatch<React.SetStateAction<string>>;
  setIsUserEditing: React.Dispatch<React.SetStateAction<boolean>>;
  isDynamicOperator: boolean;
  handleValueChange: (name: string, value: any) => void;
  trueValue: string | boolean;
  falseValue: string | boolean;
  handleSwitchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRadioChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  debouncedUpdate: (val: string) => void;
  isDtree: boolean;
  onValidationChange?: (fieldName: string, isValid: boolean) => void;
  fieldOptions?: FieldOptions;
  fieldSx?: SxProps<Theme>;
};

const DynamicFieldRenderer = memo(
  ({
    fieldConfig,
    tooltipText,
    colorText,
    setColorText,
    disabled,
    isValidColor,
    selectedValue,
    fieldText,
    setFieldText,
    setIsUserEditing,
    isDynamicOperator,
    handleValueChange,
    trueValue,
    falseValue,
    handleSwitchChange,
    handleRadioChange,
    debouncedUpdate,
    isDtree,
    onValidationChange,
    fieldOptions,
    fieldSx,
  }: DynamicFieldRendererProps) => {
    const theme = useTheme();
    const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
    const { t } = useTranslation();

    /**
     * Resolve passed fieldSx to an object for certain fields.
     * Which render direct here, will spread to mui sx prop.
     * some others pass original fieldSx and resolve in child
     */
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    /**
     * Each case in this switch represents a different field type
     * that can be rendered based on the fieldConfig type property.
     */
    switch (fieldConfig?.type) {
      case 'color': {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              label={
                <LabelWithTooltip
                  label={fieldConfig.label}
                  tooltip={tooltipText}
                />
              }
              value={colorText}
              onChange={(e) => setColorText(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={disabled}
              error={!disabled && !!colorText && !isValidColor}
              helperText={
                disabled
                  ? ''
                  : !!colorText && !isValidColor
                    ? t('blox.general.valid_hex')
                    : ''
              }
              sx={resolvedSx}
              slotProps={{
                input: {
                  endAdornment: (
                    <ColorInput
                      value={colorText}
                      onChange={setColorText}
                      width={24}
                      disabled={disabled}
                    />
                  ),
                },

                htmlInput: { maxLength: 7 },
                inputLabel: { shrink: true, sx: { zIndex: 0 } }, // TODO: Any longer valid or needed. we replaced mui input with our own.
              }}
            />
          </Box>
        );
      }

      case 'user': {
        return (
          <SelectDropdownInput
            inputLabel={fieldConfig.label}
            value={Array.isArray(selectedValue) ? selectedValue[0] || '' : selectedValue}
            updateValue={(event, values) => handleValueChange(fieldConfig.name, values)}
            fetchDataFn={fetchAndFormatAuthors}
            isDataPrivate={true}
          />
        );
      }

      case 'boolean': {
        const isSwitch =
          !fieldConfig?.config?.true_label || !fieldConfig?.config?.false_label;

        return (
          <FormControl
            component="fieldset"
            fullWidth
            margin="normal"
            sx={{ ...resolvedSx, m: 0 }}
            disabled={disabled}
          >
            {isSwitch ? (
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={
                      selectedValue === true ||
                      selectedValue === 'true' ||
                      selectedValue === trueValue
                    }
                    onChange={handleSwitchChange}
                  />
                }
                label={
                  <LabelWithTooltip
                    label={fieldConfig.label}
                    tooltip={tooltipText}
                    tooltipPlacement={fieldOptions?.tooltipPlacement}
                  />
                }
              />
            ) : (
              <RadioGroup
                value={selectedValue !== '' ? selectedValue : falseValue}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value={trueValue}
                  control={<Radio size="small" />}
                  label={fieldConfig?.config?.true_label || 'True'}
                />
                <FormControlLabel
                  value={falseValue}
                  control={<Radio size="small" />}
                  label={fieldConfig?.config?.false_label || 'False'}
                />
              </RadioGroup>
            )}
          </FormControl>
        );
      }

      case 'enumeration': {
        return (
          <EnumDynamicField
            fieldConfig={fieldConfig}
            selectedValue={selectedValue}
            disabled={disabled}
            handleValueChange={handleValueChange}
            tooltipText={tooltipText}
            fieldOptions={fieldOptions}
            fieldSx={resolvedSx}
          />
        );
      }

      case 'text':
      case 'textarea':
      case 'number': {
        return (
          <TextDynamicField
            fieldConfig={fieldConfig}
            tooltipText={tooltipText}
            fieldText={fieldText}
            setFieldText={setFieldText}
            setIsUserEditing={setIsUserEditing}
            debouncedUpdate={debouncedUpdate}
            disabled={disabled}
            isDynamicOperator={isDynamicOperator}
            fieldSx={resolvedSx}
          />
        );
      }

      case 'datetime': {
        /**
         * Note: The API intends to provide a "resolution" field to specify date or datetime
         * "resolution": "date"
         *
         * For now, We will assume date as this is the current need.
         */

        /** Note: Preserved for future update */
        // fieldInput = (
        //   <DateTimePicker
        //     label={
        //       <LabelWithTooltip
        //         label={fieldConfig.label}
        //         tooltip={tooltipText}
        //       />
        //     }
        //     value={selectedValue || null}
        //     onChange={(newValue) => handleValueChange(fieldConfig.name, newValue)}
        //     slotProps={{
        //       textField: {
        //         fullWidth: true,
        //         variant: 'outlined',
        //         sx: sxprops,
        //         InputLabelProps: {
        //           shrink: true,
        //           sx: { zIndex: 0 },
        //         },
        //       },
        //     }}
        //   />
        // );

        return (
          <DatePicker
            label={
              <LabelWithTooltip
                label={fieldConfig.label}
                tooltip={tooltipText}
              />
            }
            value={selectedValue ? dayjs(selectedValue) : null}
            disabled={disabled}
            onChange={(newValue: Dayjs | null) =>
              handleValueChange(
                fieldConfig.name,
                newValue ? newValue.format('YYYY-MM-DD') : null,
              )
            }
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
                sx: resolvedSx,
                InputLabelProps: { shrink: true, sx: { zIndex: 0 } },
              },
            }}
          />
        );
      }

      case 'multitext': {
        // Standard text array field
        return (
          <MultitextDynamicField
            fieldConfig={fieldConfig}
            selectedValue={selectedValue}
            disabled={disabled}
            handleValueChange={handleValueChange}
            fieldSx={resolvedSx}
          />
        );
      }

      case 'group': {
        // Handle date range filter specifically
        if (fieldConfig.name === 'dateRangeFilter') {
          return (
            <DateRangeField
              fieldConfig={fieldConfig}
              selectedValue={selectedValue}
              disabled={disabled}
              handleValueChange={handleValueChange}
              tooltipText={tooltipText}
              fieldSx={fieldSx}
            />
          );
        }

        // GroupDynamicField for standard group types
        return (
          <GroupDynamicField
            fieldConfig={fieldConfig}
            selectedValue={selectedValue}
            disabled={disabled}
            handleValueChange={handleValueChange}
            fieldSx={resolvedSx}
          />
        );
      }

      case 'image': {
        return (
          <ImageDynamicField
            fieldConfig={fieldConfig}
            value={selectedValue}
            handleValueChange={handleValueChange}
            disabled={disabled}
            fieldSx={resolvedSx}
          />
        );
      }

      default: {
        // Handle denum, dtree, and others that are secret denum/dtree
        if (fieldConfig.config?.resolver && fieldConfig.config?.app) {
          if (isDtree) {
            return (
              <DenumTreeField
                value={selectedValue}
                onChange={handleValueChange}
                sx={resolvedSx}
                disabled={isDynamicOperator || disabled}
              />
            );
          } else {
            return (
              <DenumField
                value={selectedValue}
                onChange={handleValueChange}
                onValidationChange={onValidationChange}
                disabled={disabled}
                fieldOptions={fieldOptions}
                fieldSx={resolvedSx}
              />
            );
          }
        }

        // Fallback message for unsupported field types
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              ...resolvedSx,
            }}
          >
            <TextField
              label={fieldConfig.label}
              variant="outlined"
              value={selectedValue || ''}
              disabled
            />
            <Tooltip
              title={`"${fieldConfig.type}" field coming soon...`}
              disableInteractive
              placement="top"
            >
              <ConstructionIcon
                fontSize="small"
                color="info"
              />
            </Tooltip>
          </Box>
        );
      }
    }
  },
);
DynamicFieldRenderer.displayName = 'DynamicFieldRenderer';

/**
 * ---------------------------------------
 * DynamicFormFieldWithContext
 * ---------------------------------------
 * Wrapper around DynamicFormField that provides
 * necessary context from DynamicFieldProvider.
 * Type is union of both Field and Context props.
 * Some props for context, others to the field.
 */

export type DynamicFormFieldWithContextProps = DynamicFieldProviderProps &
  DynamicFormFieldProps;

export const DynamicFormFieldWithContext = memo(
  ({
    fieldConfig,
    initDynamicOpts,
    onDynamicOptsChange,
    fieldData,
    operations,
    fieldOptions,
    fieldSx,
    onChangeOperation,
    onValidationChange,
    value,
    onChange,
    disabled,
  }: DynamicFormFieldWithContextProps) => {
    return (
      <DynamicFieldProvider
        fieldConfig={fieldConfig}
        initDynamicOpts={initDynamicOpts}
        onDynamicOptsChange={onDynamicOptsChange}
      >
        <DynamicFormField
          fieldData={fieldData}
          operations={operations}
          fieldOptions={fieldOptions}
          fieldSx={fieldSx}
          onChangeOperation={onChangeOperation}
          onValidationChange={onValidationChange}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      </DynamicFieldProvider>
    );
  },
);
DynamicFormFieldWithContext.displayName = 'DynamicFormFieldWithContext';
